/* DRAFT — PENDING LEGAL REVIEW */
import React from "react";
import PageShell from "../components/layout/PageShell";
import Container from "../components/primitives/Container";
import Section from "../components/primitives/Section";
import Eyebrow from "../components/primitives/Eyebrow";
import Reveal from "../components/primitives/Reveal";
import SeoHead from "../components/seo/SeoHead";
import DraftBanner from "../components/legal/DraftBanner";

const H2 = ({ children }) => (
  <h2
    className="mt-12 font-serif font-normal text-navy text-balance"
    style={{ fontSize: "clamp(26px, 3vw, 36px)", lineHeight: 1.2, letterSpacing: "-0.005em" }}
  >
    {children}
  </h2>
);

const P = ({ children, ...rest }) => (
  <p
    {...rest}
    className="mt-6 font-sans text-[16px] sm:text-[16.5px] leading-[1.75] text-navy/80 max-w-prose"
  >
    {children}
  </p>
);

const Privacy = () => {
  return (
    <PageShell>
      <SeoHead
        title="Privacy Policy"
        description="How Quantum US Capital Partners collects, uses, and safeguards information in connection with this website."
        path="/legal/privacy"
      >
        <meta name="x-document-status" content="draft-pending-legal-review" />
      </SeoHead>

      <Section tone="light" className="pt-28 md:pt-32 lg:pt-36 pb-20 md:pb-24">
        <Container>
          <Reveal className="max-w-3xl">
            <DraftBanner />
            <div className="mt-8">
              <Eyebrow tone="muted">Legal</Eyebrow>
            </div>
            <h1
              className="mt-8 font-serif font-normal text-navy text-balance"
              data-testid="privacy-title"
              style={{ fontSize: "clamp(40px, 5.5vw, 80px)", lineHeight: 1.05, letterSpacing: "-0.01em" }}
            >
              Privacy Policy.
            </h1>
            <span aria-hidden="true" className="mt-10 block h-px w-12 bg-gold" />
            <p
              className="mt-10 font-sans uppercase text-navy/55"
              style={{ fontSize: "11px", letterSpacing: "0.22em" }}
              data-testid="privacy-effective"
            >
              Effective February 2026
            </p>
          </Reveal>

          <Reveal className="mt-10 max-w-3xl">
            <P>
              This Privacy Policy describes how Quantum US Capital Partners LLC
              (&ldquo;Quantum,&rdquo; &ldquo;we,&rdquo; &ldquo;our&rdquo;)
              collects, uses, and safeguards information in connection with the
              quscap.com website. It is provided in keeping with our
              long-standing posture of restraint with respect to data: we
              collect only what is necessary, use it only for the purposes
              described here, and retain it only as long as it remains
              materially useful.
            </P>

            <H2>Information We Collect</H2>
            <P>
              We collect two categories of information. The first consists of
              information you provide directly &mdash; for example, when you
              submit an inquiry through our Contact page, the name, company,
              email address, telephone number, and message you elect to share
              with us. The second consists of technical information generated
              automatically when you visit the site, such as your internet
              protocol address, browser type, referring URL, the pages you
              view, and timestamps of your visit.
            </P>

            <H2>How We Use Information</H2>
            <P>
              Inquiries submitted through the Contact page are used to respond
              to you, to maintain a record of correspondence, and, where
              appropriate, to evaluate potential professional engagement.
              Technical information is used to operate the site reliably, to
              diagnose problems, to protect against abuse, and &mdash; with
              your explicit consent &mdash; to understand in aggregate how the
              site is used.
            </P>

            <H2>Cookies</H2>
            <P>
              We use a small number of cookies that are strictly necessary for
              the site to function (for example, to maintain a secure
              administrative session). Strictly-necessary cookies do not
              require your consent and cannot be disabled without rendering
              parts of the site unusable.
            </P>
            <P>
              We also use analytics cookies provided by Google Analytics (GA4)
              to understand how visitors engage with our material in aggregate.
              These cookies are loaded only after you accept them via the
              cookie notice; if you decline, no analytics cookies are placed
              and no analytics data is collected. Your decision is stored in
              your browser&rsquo;s local storage and respected on subsequent
              visits.
            </P>

            <H2>Sharing of Information</H2>
            <P>
              We do not sell personal information. We may share information
              with service providers that operate elements of our
              infrastructure (for example, hosting and analytics vendors)
              under written terms that restrict their use of the information
              to providing services to us. We may also disclose information
              where required by applicable law, by a court of competent
              jurisdiction, or to protect our rights or those of our
              counterparties.
            </P>

            <H2>Data Retention</H2>
            <P>
              Inquiries submitted through the Contact page are retained for so
              long as they remain useful to the relationship, after which they
              are deleted or anonymised. Technical and analytics information,
              where collected with consent, is retained for the period
              configured within the analytics provider&rsquo;s default
              settings and is not used to construct individual profiles.
            </P>

            <H2>Security</H2>
            <P>
              We maintain administrative, technical, and organisational
              measures designed to protect information against unauthorised
              access, alteration, disclosure, or destruction. No information
              system can be guaranteed to be entirely secure, and we therefore
              do not warrant absolute security.
            </P>

            <H2>Your Choices</H2>
            <P>
              You may decline analytics cookies at any time by using the
              decline option in the cookie notice or by clearing your browser
              storage. You may request access to, correction of, or deletion
              of information you have provided to us by writing to the
              address below. We will respond to reasonable requests within a
              reasonable period.
            </P>

            <H2>International Considerations</H2>
            <P>
              Our infrastructure operates in jurisdictions that may differ
              from your country of residence. By using this site, you
              acknowledge that information may be processed outside your home
              jurisdiction in accordance with the safeguards described in
              this policy.
            </P>

            <H2>Changes to this Policy</H2>
            <P>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices or applicable law. The effective date
              above will be revised accordingly. Material changes will be
              announced through reasonable means.
            </P>

            <H2>Contact</H2>
            <P>
              Inquiries regarding this Privacy Policy may be addressed to the
              Office of the Chairman at{" "}
              <a
                href="mailto:chairman@quscap.com"
                className="text-navy underline decoration-gold underline-offset-4 hover:text-gold transition-colors"
                data-testid="privacy-contact-email"
              >
                chairman@quscap.com
              </a>
              .
            </P>
          </Reveal>
        </Container>
      </Section>
    </PageShell>
  );
};

export default Privacy;
